import { Button } from '@newfront-insurance/core-ui/v2';
import { useState } from 'react';

import { TeamCreateView } from './team-create-view';
import { TeamList } from './team-list';
import { TeamSettingsDetail } from './team-settings-detail';
import type { SettingsGroup } from '../../../../queries/skill-administration';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { SettingsCreateButton, SettingsPageHeader } from '../settings-page-header';
import { useSettingsPanelContextSafe } from '../settings-panel-context';
import { SettingsSectionList, SettingsSectionListRow } from '../settings-section-list';

export function TeamsSettings() {
  const { settingsAdministration, userUuid } = useChatOverlayContext();
  const { status, teams, retry, organizations } = settingsAdministration;
  const settingsPanel = useSettingsPanelContextSafe();
  const [localCreation, setLocalCreation] = useState<{ userUuid: string } | null>(null);
  // Selection belongs to the current discovery result, so a new session cannot reopen an old team.
  const [selection, setSelection] = useState<{ uuid: string; teams: readonly SettingsGroup[] } | null>(null);
  const selectedTeam =
    status === 'ready' && selection?.teams === teams ? teams.find((team) => team.uuid === selection.uuid) : undefined;
  if (localCreation) {
    return <TeamCreateView {...localCreation} onBack={() => setLocalCreation(null)} />;
  }
  if (selectedTeam) {
    return (
      <TeamSettingsDetail
        key={`${userUuid}:${selectedTeam.uuid}`}
        team={selectedTeam}
        onBack={() => setSelection(null)}
      />
    );
  }
  return (
    <section aria-labelledby="settings-teams-title" className="flex min-w-0 flex-col gap-6">
      <SettingsPageHeader
        title="Teams"
        titleId="settings-teams-title"
        description="Groups you create and maintain for sharing skills."
        action={
          <SettingsCreateButton
            testId="settings-new-team-button"
            disabled={status !== 'ready' || !organizations.length || !userUuid}
            onClick={() => {
              if (status !== 'ready' || !organizations.length || !userUuid) return;
              const view = { type: 'team-create' as const, userUuid };
              if (settingsPanel) settingsPanel.pushView(view);
              else setLocalCreation(view);
            }}
          >
            New team
          </SettingsCreateButton>
        }
      />
      {status === 'loading' ? (
        <SettingsSectionList title="Teams" loading>
          <SettingsSectionListRow loadingLabel="Loading teams" />
        </SettingsSectionList>
      ) : status === 'error' ? (
        <div role="alert" className="border-steel-75 text-steel-400 rounded-md border bg-white p-5 text-sm">
          Couldn't load teams.{' '}
          <Button type="button" variant="inline" onClick={retry} className="text-sm">
            Retry
          </Button>
        </div>
      ) : organizations.length ? (
        organizations.map((organization) => (
          <TeamList
            key={organization.uuid}
            title={organization.name}
            teams={teams.filter((team) => team.parentGroupUuid === organization.uuid)}
            onSelectTeam={(team) => setSelection({ uuid: team.uuid, teams })}
          />
        ))
      ) : (
        <div className="border-steel-75 rounded-md border bg-white px-5 py-6">
          <h3 className="text-steel-500 text-sm font-semibold">No teams to display.</h3>
          <p className="text-steel-400 mt-1 text-sm leading-5">You don't manage any child teams yet.</p>
        </div>
      )}
    </section>
  );
}
